import { Component } from '@theme/component'

export const PRODUCT_FUNC_SLIDER_TAG = 'product-func-slider'

export const SLIDER_EDGE_TOLERANCE_PX = 2

export const SLIDER_DRAG_THRESHOLD_PX = 6

/** A drag past this share of one card moves to the next card. */
export const SLIDER_DRAG_COMMIT_RATIO = 0.15

/** Release speed (px/ms) that counts as a flick. */
export const SLIDER_FLICK_VELOCITY = 0.4

const SNAP_SETTLE_FALLBACK_MS = 450

/**
 * @param {string | null | undefined} gapValue
 * @returns {number}
 */
export function sliderGapPx(gapValue) {
  const parsed = Number.parseFloat(String(gapValue ?? ''))

  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
}

/**
 * @param {number} cardWidth
 * @param {number} gap
 * @returns {number}
 */
export function sliderCardStep(cardWidth, gap) {
  const width = Number(cardWidth)

  if (!Number.isFinite(width) || width <= 0) return 0

  return width + sliderGapPx(String(gap))
}

/**
 * @param {number} scrollWidth
 * @param {number} clientWidth
 * @param {number} [endSpacer] - Trailing spacer that lets the last card align
 * @returns {boolean}
 */
export function sliderContentFits(scrollWidth, clientWidth, endSpacer = 0) {
  const spacer = Number(endSpacer)

  return (
    Number(scrollWidth) -
      (Number.isFinite(spacer) && spacer > 0 ? spacer : 0) <=
    Number(clientWidth) + SLIDER_EDGE_TOLERANCE_PX
  )
}

/**
 * Card index nearest a scroll offset.
 * @param {number} scrollLeft
 * @param {number} step
 * @returns {number}
 */
export function sliderIndexFromScroll(scrollLeft, step) {
  if (!(step > 0)) return 0

  return Math.max(0, Math.round(Number(scrollLeft) / step))
}

/**
 * Scroll offset for a card index, clamped to the scrollport.
 * @param {number} index
 * @param {number} step
 * @param {number} maxLeft
 * @returns {number}
 */
export function sliderScrollTarget(index, step, maxLeft) {
  const max = Math.max(0, Number(maxLeft) || 0)

  if (!(step > 0)) return 0

  return Math.min(Math.max(0, Math.round(Number(index))) * step, max)
}

/**
 * @param {number} scrollLeft
 * @returns {boolean}
 */
export function sliderCanScrollPrev(scrollLeft) {
  return Number(scrollLeft) > SLIDER_EDGE_TOLERANCE_PX
}

/**
 * @param {number} scrollLeft
 * @param {number} scrollWidth
 * @param {number} clientWidth
 * @returns {boolean}
 */
export function sliderCanScrollNext(scrollLeft, scrollWidth, clientWidth) {
  return (
    Number(scrollLeft) + Number(clientWidth) <
    Number(scrollWidth) - SLIDER_EDGE_TOLERANCE_PX
  )
}

/**
 * @param {number} value
 * @param {number} maxLeft
 * @returns {number}
 */
function clampScrollLeft(value, maxLeft) {
  return Math.min(Math.max(0, value), Math.max(0, Number(maxLeft) || 0))
}

/**
 * Scroll position that follows the pointer while dragging.
 * @param {number} startLeft - `scrollLeft` when the drag started
 * @param {number} deltaX - Pointer x movement since the drag started
 * @param {number} maxLeft - `scrollWidth - clientWidth`
 * @returns {number}
 */
export function sliderDragScrollLeft(startLeft, deltaX, maxLeft) {
  return clampScrollLeft(Number(startLeft) - Number(deltaX), maxLeft)
}

/**
 * Which way a released drag should settle: 1 forward, -1 back, 0 nearest.
 * @param {number} deltaX - Pointer x movement over the whole drag
 * @param {number} velocity - Pointer x speed at release, px/ms
 * @param {number} step - One card plus the gap
 * @returns {-1 | 0 | 1}
 */
export function sliderDragDirection(deltaX, velocity, step) {
  const distance = Number(deltaX)
  const speed = Number(velocity)

  if (Math.abs(speed) >= SLIDER_FLICK_VELOCITY) return speed < 0 ? 1 : -1

  if (step > 0 && Math.abs(distance) >= step * SLIDER_DRAG_COMMIT_RATIO) {
    return distance < 0 ? 1 : -1
  }

  return 0
}

/**
 * Card-aligned scroll position to settle on after a drag.
 * @param {number} scrollLeft - Current `scrollLeft`
 * @param {number} step - One card plus the gap
 * @param {number} maxLeft - `scrollWidth - clientWidth`
 * @param {-1 | 0 | 1} [direction] - Bias from `sliderDragDirection`
 * @returns {number}
 */
export function sliderSnapLeft(scrollLeft, step, maxLeft, direction = 0) {
  const left = Number(scrollLeft)

  if (!(step > 0)) return clampScrollLeft(left, maxLeft)

  const index = left / step
  const snapped =
    direction > 0
      ? Math.ceil(index)
      : direction < 0
        ? Math.floor(index)
        : Math.round(index)

  return clampScrollLeft(snapped * step, maxLeft)
}

/**
 * Pointer capture throws when the pointer is no longer active.
 * @param {HTMLElement} element
 * @param {number} pointerId
 * @param {boolean} capture
 */
function setCapture(element, pointerId, capture) {
  try {
    if (capture) {
      element.setPointerCapture?.(pointerId)
    } else if (element.hasPointerCapture?.(pointerId)) {
      element.releasePointerCapture(pointerId)
    }
  } catch {
    // The drag still works without capture while the pointer stays over the row.
  }
}

/**
 * @typedef {Object} DragState
 * @property {number} pointerId
 * @property {number} startX
 * @property {number} startY
 * @property {number} startLeft
 * @property {number} lastX
 * @property {number} lastTime
 * @property {number} velocity
 * @property {boolean} dragging
 */

/**
 * @typedef {Object} ProductFuncSliderRefs
 * @property {HTMLElement} [scroll] - Horizontal scrollport
 * @property {HTMLElement} [track] - Card row
 * @property {HTMLElement} [nav] - Arrow row
 * @property {HTMLButtonElement} [prev] - Previous card
 * @property {HTMLButtonElement} [next] - Next card
 */

/**
 * Arrow carousel for the functionality slider.
 * A finger scrolls the row natively. A mouse can drag it.
 * Arrows move one card and stack when pressed again mid-scroll.
 * @extends {Component<ProductFuncSliderRefs>}
 */
export class ProductFuncSlider extends Component {
  /** @type {ResizeObserver | null} */
  #observer = null

  /** @type {AbortController | null} */
  #listeners = null

  /** @type {DragState | null} */
  #drag = null

  /** @type {number | null} */
  #settleTimer = null

  /** @type {(() => void) | null} */
  #settleRelease = null

  /** @type {number | null} */
  #pendingLeft = null

  #fits = false

  #suppressClick = false

  /** @type {number} */
  #syncRafId = 0

  connectedCallback() {
    super.connectedCallback()
    this.#sync()

    if (typeof ResizeObserver === 'function') {
      this.#observer = new ResizeObserver(() => {
        this.#sync()
      })
      this.#observer.observe(this)
    }

    const scroll = this.#scrollEl()

    if (!scroll) return

    this.#listeners = new AbortController()
    const { signal } = this.#listeners

    scroll.addEventListener('scroll', () => this.#requestSyncEdges(), {
      passive: true,
      signal
    })
    scroll.addEventListener('pointerdown', this.#onPointerDown, { signal })
    scroll.addEventListener('pointermove', this.#onPointerMove, { signal })
    scroll.addEventListener('pointerup', this.#onPointerEnd, { signal })
    scroll.addEventListener('pointercancel', this.#onPointerEnd, { signal })
    scroll.addEventListener('dragstart', (event) => event.preventDefault(), {
      signal
    })
    scroll.addEventListener('click', this.#onClickCapture, {
      capture: true,
      signal
    })
    scroll.setAttribute('data-drag-ready', '')
  }

  disconnectedCallback() {
    this.#observer?.disconnect()
    this.#observer = null
    this.#cancelSettle()
    if (this.#syncRafId) {
      window.cancelAnimationFrame(this.#syncRafId)
      this.#syncRafId = 0
    }
    this.#listeners?.abort()
    this.#listeners = null
    this.#drag = null
    this.#pendingLeft = null
    this.#scrollEl()?.removeAttribute('data-dragging')
    super.disconnectedCallback()
  }

  scrollPrev() {
    this.#scrollBy(-1)
  }

  scrollNext() {
    this.#scrollBy(1)
  }

  /**
   * Arrow keys on the scrollport move one card.
   * @param {KeyboardEvent} event
   */
  onScrollKeydown(event) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return

    event.preventDefault()

    if (event.key === 'ArrowRight') {
      this.scrollNext()
      return
    }

    this.scrollPrev()
  }

  #sync() {
    const scroll = this.#scrollEl()

    if (!scroll) return

    const end = scroll.querySelector('[data-slider-end]')
    const endWidth = end instanceof HTMLElement ? end.offsetWidth : 0
    const fits = sliderContentFits(
      scroll.scrollWidth,
      scroll.clientWidth,
      endWidth
    )

    this.#fits = fits
    scroll.toggleAttribute('data-fits', fits)

    if (fits) {
      scroll.removeAttribute('tabindex')
    } else if (scroll.getAttribute('tabindex') !== '0') {
      scroll.tabIndex = 0
    }

    const nav = this.refs.nav

    if (nav instanceof HTMLElement) nav.hidden = fits

    this.#syncEdges()
  }

  #requestSyncEdges() {
    if (this.#syncRafId) return

    this.#syncRafId = window.requestAnimationFrame(() => {
      this.#syncRafId = 0
      this.#syncEdges()
    })
  }

  #syncEdges() {
    const scroll = this.#scrollEl()

    if (!scroll) return

    const left = this.#pendingLeft ?? scroll.scrollLeft
    const prev = this.refs.prev
    const next = this.refs.next

    if (prev instanceof HTMLButtonElement) {
      prev.disabled = !sliderCanScrollPrev(left)
    }

    if (next instanceof HTMLButtonElement) {
      next.disabled = !sliderCanScrollNext(
        left,
        scroll.scrollWidth,
        scroll.clientWidth
      )
    }

    const atEnd =
      next instanceof HTMLButtonElement
        ? next.disabled
        : !sliderCanScrollNext(left, scroll.scrollWidth, scroll.clientWidth)

    if (!this.#fits && atEnd) {
      scroll.setAttribute('data-at-end', '')
    } else {
      scroll.removeAttribute('data-at-end')
    }
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (this.#fits || !event.isPrimary) return
    if (event.pointerType !== 'mouse' || event.button !== 0) return

    const target = event.target

    if (
      target instanceof Element &&
      target.closest('a, button, input, select, textarea, label')
    ) {
      return
    }

    const scroll = this.#scrollEl()

    if (!scroll) return

    this.#cancelSettle()
    this.#pendingLeft = null
    this.#drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startLeft: scroll.scrollLeft,
      lastX: event.clientX,
      lastTime: event.timeStamp,
      velocity: 0,
      dragging: false
    }
  }

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    const drag = this.#drag
    const scroll = this.#scrollEl()

    if (!drag || !scroll || event.pointerId !== drag.pointerId) return

    const deltaX = event.clientX - drag.startX
    const deltaY = event.clientY - drag.startY

    if (!drag.dragging) {
      if (
        Math.abs(deltaX) < SLIDER_DRAG_THRESHOLD_PX &&
        Math.abs(deltaY) < SLIDER_DRAG_THRESHOLD_PX
      ) {
        return
      }

      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        this.#drag = null
        return
      }

      drag.dragging = true
      drag.startX = event.clientX
      drag.lastX = event.clientX
      drag.lastTime = event.timeStamp
      scroll.setAttribute('data-dragging', '')
      window.getSelection?.()?.removeAllRanges()
      setCapture(scroll, event.pointerId, true)
      return
    }

    event.preventDefault()

    const elapsed = event.timeStamp - drag.lastTime

    if (elapsed > 0) {
      drag.velocity = (event.clientX - drag.lastX) / elapsed
    }

    drag.lastX = event.clientX
    drag.lastTime = event.timeStamp
    scroll.scrollLeft = sliderDragScrollLeft(
      drag.startLeft,
      event.clientX - drag.startX,
      scroll.scrollWidth - scroll.clientWidth
    )
  }

  /** @param {PointerEvent} event */
  #onPointerEnd = (event) => {
    const drag = this.#drag
    const scroll = this.#scrollEl()

    if (!drag || event.pointerId !== drag.pointerId) return

    this.#drag = null

    if (!drag.dragging || !scroll) return

    setCapture(scroll, event.pointerId, false)
    this.#suppressClick = true
    window.setTimeout(() => {
      this.#suppressClick = false
    }, 0)

    const step = this.#step()
    const idle = event.timeStamp - drag.lastTime > 100
    const direction = sliderDragDirection(
      drag.lastX - drag.startX,
      idle ? 0 : drag.velocity,
      step
    )
    const left = sliderSnapLeft(
      scroll.scrollLeft,
      step,
      scroll.scrollWidth - scroll.clientWidth,
      event.type === 'pointercancel' ? 0 : direction
    )

    this.#settleTo(scroll, left)
  }

  /** @param {MouseEvent} event */
  #onClickCapture = (event) => {
    if (!this.#suppressClick) return

    this.#suppressClick = false
    event.preventDefault()
    event.stopPropagation()
  }

  /**
   * Scrolls to a card edge with snapping still off, then turns snap back on.
   * @param {HTMLElement} scroll
   * @param {number} left
   */
  #settleTo(scroll, left) {
    this.#pendingLeft = left
    this.#cancelSettle()

    const behavior = this.#scrollBehavior()
    const release = () => {
      this.#cancelSettle()
      this.#pendingLeft = null
      scroll.removeAttribute('data-dragging')
      this.#syncEdges()
    }

    if (typeof scroll.scrollTo === 'function') {
      scroll.scrollTo({ left, behavior })
    } else {
      scroll.scrollLeft = left
    }

    if (behavior === 'auto' || Math.abs(scroll.scrollLeft - left) < 1) {
      release()
      return
    }

    this.#settleRelease = release
    scroll.addEventListener('scrollend', release, { once: true })
    this.#settleTimer = window.setTimeout(release, SNAP_SETTLE_FALLBACK_MS)
  }

  #cancelSettle() {
    window.clearTimeout(this.#settleTimer ?? undefined)
    this.#settleTimer = null

    if (this.#settleRelease) {
      this.#scrollEl()?.removeEventListener('scrollend', this.#settleRelease)
      this.#settleRelease = null
    }
  }

  /**
   * @returns {ScrollBehavior}
   */
  #scrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth'
  }

  /**
   * @param {number} direction
   */
  #scrollBy(direction) {
    const scroll = this.#scrollEl()

    if (!scroll) return

    const step = this.#step()

    if (step <= 0) return

    const maxLeft = Math.max(0, scroll.scrollWidth - scroll.clientWidth)
    const origin = this.#pendingLeft ?? scroll.scrollLeft
    const left = sliderScrollTarget(
      sliderIndexFromScroll(origin, step) + direction,
      step,
      maxLeft
    )

    this.#settleTo(scroll, left)
    this.#syncEdges()
  }

  /**
   * @returns {number}
   */
  #step() {
    const track = this.refs.track
    const card =
      track instanceof HTMLElement
        ? track.querySelector('[data-slider-card]')
        : null

    if (!(card instanceof HTMLElement) || !(track instanceof HTMLElement)) {
      return 0
    }

    const gap = sliderGapPx(getComputedStyle(track).gap)

    return sliderCardStep(card.offsetWidth, gap)
  }

  /**
   * @returns {HTMLElement | null}
   */
  #scrollEl() {
    const scroll = this.refs.scroll

    return scroll instanceof HTMLElement ? scroll : null
  }
}

if (!customElements.get(PRODUCT_FUNC_SLIDER_TAG)) {
  customElements.define(PRODUCT_FUNC_SLIDER_TAG, ProductFuncSlider)
}
